/**
 * Web应用特定的翻译历史/缓存上下文提供者
 * 消费核心HistoryContext，并为Web阅读页面管理活动文档的翻译缓存。
 */

import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
  createContext,
  useContext,
  useRef,
} from 'react';
import {
  TextSelectionTranslation,
  TranslationResponse,
  ITranslationHistoryContext as CoreHistoryContextType,
  DocumentIdentitySource,
  DocumentIdentifier,
  SelectionData,
} from '@readsavor/types';
import {
  useHistoryContext as useCoreHistoryContext,
  useDocumentControl,
  useErrorHandler,
  useLanguageState,
  useModelState,
} from '@readsavor/core';
import { createCacheEntry, generateCacheKey } from '@readsavor/utils';

// 1. Define the Web-specific Context and its type
interface IWebActiveDocTranslationContext {
  activeIdentity: DocumentIdentifier | null;
  currentDocTranslatedWords: TextSelectionTranslation[];
  isCurrentDocCacheLoading: boolean;
  findInCurrentDocCache: (selection: SelectionData) => TextSelectionTranslation | null;
  saveNewTranslationForCurrentDoc: (
    selection: SelectionData,
    response: TranslationResponse
  ) => Promise<TextSelectionTranslation | null>;
  searchVocabulary: CoreHistoryContextType['searchVocabulary'];
  getAllVocabulary: CoreHistoryContextType['getAllVocabulary'];
  deleteVocabularyItem: CoreHistoryContextType['deleteVocabularyItem'];
  refreshStats: CoreHistoryContextType['refreshStats'];
  getGlobalVocabularyCount: () => number;
}

const WebActiveDocTranslationContext = createContext<IWebActiveDocTranslationContext | undefined>(
  undefined
);

export const useWebActiveDocTranslationContext = (): IWebActiveDocTranslationContext => {
  const context = useContext(WebActiveDocTranslationContext);
  if (!context) {
    throw new Error(
      'useWebActiveDocTranslationContext must be used within a WebTranslationHistoryProvider'
    );
  }
  return context;
};

interface WebTranslationHistoryProviderProps {
  children: ReactNode;
}

export const WebTranslationHistoryProvider: React.FC<WebTranslationHistoryProviderProps> = ({
  children,
}) => {
  const coreHistoryCtx = useCoreHistoryContext();
  const errorHandler = useErrorHandler();
  const { currentIdentity: activeIdentityFromUrl } = useDocumentControl({
    source: DocumentIdentitySource.ROUTE,
    debug: { logSource: 'WebHistProv' },
  });

  const [currentDocTranslatedWords, setCurrentDocTranslatedWords] = useState<
    TextSelectionTranslation[]
  >([]);
  const [isCacheLoading, setIsCacheLoading] = useState<boolean>(false);

  const { sourceLanguage: globalSourceLanguage, targetLanguage: globalTargetLanguage } =
    useLanguageState();
  const { model: globalModel } = useModelState();

  const loadDocTranslations = coreHistoryCtx.loadTranslationsForDocument;
  useEffect(() => {
    let isMounted = true;
    if (activeIdentityFromUrl) {
      setIsCacheLoading(true);
      loadDocTranslations(activeIdentityFromUrl)
        .then(words => {
          if (isMounted) {
            setCurrentDocTranslatedWords(words || []);
            setIsCacheLoading(false);
          }
        })
        .catch(err => {
          if (isMounted) {
            setIsCacheLoading(false);
            setCurrentDocTranslatedWords([]);
          }
          errorHandler.handleError(err, {
            component: 'WebTranslationHistoryProvider',
            operation: 'loadTranslationsForDocument Effect',
          });
        });
    } else {
      setCurrentDocTranslatedWords([]);
      setIsCacheLoading(false);
    }
    return () => {
      isMounted = false;
    };
  }, [activeIdentityFromUrl, loadDocTranslations, errorHandler]);

  const prevActiveIdentityRef = useRef<DocumentIdentifier | null>(null);
  const clearCoreDocCache = coreHistoryCtx.clearCurrentDocumentCache;
  useEffect(() => {
    const identityToClear = prevActiveIdentityRef.current;
    if (
      identityToClear &&
      (identityToClear.id !== activeIdentityFromUrl?.id ||
        identityToClear.type !== activeIdentityFromUrl?.type)
    ) {
      clearCoreDocCache(identityToClear);
    }
    prevActiveIdentityRef.current = activeIdentityFromUrl;
    return () => {
      if (prevActiveIdentityRef.current) {
        clearCoreDocCache(prevActiveIdentityRef.current);
      }
    };
  }, [activeIdentityFromUrl, clearCoreDocCache]);

  const coreSaveTranslation = coreHistoryCtx.saveTranslation;
  const saveNewTranslationForCurrentDoc = useCallback(
    async (
      selection: SelectionData,
      response: TranslationResponse
    ): Promise<TextSelectionTranslation | null> => {
      if (!activeIdentityFromUrl) {
        errorHandler.handleError('Cannot save translation: No active document identity.', {
          component: 'WebTranslationHistoryProvider',
          operation: 'saveNewTranslationForCurrentDoc',
        });
        return null;
      }

      const translationDataToSave = createCacheEntry({
        documentIdentity: activeIdentityFromUrl,
        selection: selection,
        translationResponse: response,
        sourceLanguage: globalSourceLanguage || 'auto',
        targetLanguage: globalTargetLanguage || 'en',
      });

      const entryWithModel: Omit<TextSelectionTranslation, 'orderId' | 'id' | 'timestamp'> = {
        ...translationDataToSave,
        model: globalModel || undefined,
      };

      try {
        const fullSavedTranslation = await coreSaveTranslation(entryWithModel);
        if (fullSavedTranslation) {
          setCurrentDocTranslatedWords(prevWords => {
            const index = prevWords.findIndex(w => w.id === fullSavedTranslation.id);
            let newWords;
            if (index !== -1) {
              newWords = [...prevWords];
              newWords[index] = fullSavedTranslation;
            } else {
              newWords = [...prevWords, fullSavedTranslation];
            }
            return newWords;
          });
        }
        return fullSavedTranslation;
      } catch (error) {
        errorHandler.handleError(error instanceof Error ? error : String(error), {
          component: 'WebTranslationHistoryProvider',
          operation: 'saveNewTranslationForCurrentDoc',
          userMessage: '保存新翻译失败。',
        });
        return null;
      }
    },
    [
      activeIdentityFromUrl,
      coreSaveTranslation,
      errorHandler,
      globalSourceLanguage,
      globalTargetLanguage,
      globalModel,
    ]
  );

  const findInCurrentDocCache = useCallback(
    (selection: SelectionData): TextSelectionTranslation | null => {
      if (!activeIdentityFromUrl) {
        return null;
      }

      const cacheKey = generateCacheKey(selection, activeIdentityFromUrl);
      const result = currentDocTranslatedWords.find(t => t.id === cacheKey) || null;

      return result;
    },
    [activeIdentityFromUrl, currentDocTranslatedWords]
  );

  const contextValue: IWebActiveDocTranslationContext = useMemo(() => {
    return {
      activeIdentity: activeIdentityFromUrl,
      currentDocTranslatedWords,
      isCurrentDocCacheLoading: isCacheLoading || coreHistoryCtx.historyState.isLoading,
      findInCurrentDocCache,
      saveNewTranslationForCurrentDoc,
      searchVocabulary: coreHistoryCtx.searchVocabulary,
      getAllVocabulary: coreHistoryCtx.getAllVocabulary,
      deleteVocabularyItem: coreHistoryCtx.deleteVocabularyItem,
      refreshStats: coreHistoryCtx.refreshStats,
      getGlobalVocabularyCount: () => coreHistoryCtx.historyState.totalVocabularyCount,
    };
  }, [
    activeIdentityFromUrl,
    currentDocTranslatedWords,
    isCacheLoading,
    coreHistoryCtx.historyState.isLoading,
    coreHistoryCtx.historyState.totalVocabularyCount,
    findInCurrentDocCache,
    saveNewTranslationForCurrentDoc,
    coreHistoryCtx.searchVocabulary,
    coreHistoryCtx.getAllVocabulary,
    coreHistoryCtx.deleteVocabularyItem,
    coreHistoryCtx.refreshStats,
  ]);

  return (
    <WebActiveDocTranslationContext.Provider value={contextValue}>
      {children}
    </WebActiveDocTranslationContext.Provider>
  );
};

export default WebTranslationHistoryProvider;
